import { Navigate } from 'react-router-dom';
import { ReactNode } from 'react';
import { useUserProfile } from '../../contexts/UserProfileContext';
import AppConfig from '../../AppConfig';
import LoadingSpinner from '../LoadingSpinner';

type AccessCheck =
  | 'dashboard'
  | 'accounts'
  | 'users'
  | 'contentManagement'
  | 'templates'
  | 'reviewContent'
  | 'courses'
  | 'settings'
  | 'aiRecommendation'
  | 'trainingPlanGateway';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Specify which access check to perform */
  requiredAccess: AccessCheck;
}

/**
 * ProtectedRoute component that guards routes based on user permissions.
 *
 * Usage:
 * - <ProtectedRoute requiredAccess="dashboard">
 * - <ProtectedRoute requiredAccess="accounts">
 * - <ProtectedRoute requiredAccess="users">
 * - <ProtectedRoute requiredAccess="contentManagement">
 * - <ProtectedRoute requiredAccess="templates">
 * - <ProtectedRoute requiredAccess="reviewContent">
 * - <ProtectedRoute requiredAccess="courses">
 * - <ProtectedRoute requiredAccess="settings"> (requires canAccessSettings and AppConfig.userSettingsEnabled; else /unauthorized)
 * - <ProtectedRoute requiredAccess="aiRecommendation"> (admin dev tools OR presales sales-ops org)
 * - <ProtectedRoute requiredAccess="trainingPlanGateway"> (Spark super_admin OR presales AI recommendation)
 *
 * @param children - The component to render if access is granted
 * @param requiredAccess - Type of access required to view this route
 */
export function ProtectedRoute({ children, requiredAccess }: ProtectedRouteProps) {
  const {
    isLoading,
    canAccessDashboard,
    canAccessAccounts,
    canAccessUsers,
    canAccessContentManagement,
    canAccessTemplates,
    canReviewContent,
    canAccessCourses,
    canAccessSettings,
    canAccessAiRecommendation,
    canAccessTrainingPlanGateway,
  } = useUserProfile();

  // Show loading spinner while checking permissions
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Check access based on requiredAccess type
  let hasAccess = false;

  switch (requiredAccess) {
    case 'dashboard':
      hasAccess = canAccessDashboard;
      break;
    case 'accounts':
      hasAccess = canAccessAccounts;
      break;
    case 'users':
      hasAccess = canAccessUsers;
      break;
    case 'contentManagement':
      hasAccess = canAccessContentManagement;
      break;
    case 'templates':
      hasAccess = canAccessTemplates;
      break;
    case 'reviewContent':
      hasAccess = canReviewContent;
      break;
    case 'courses':
      hasAccess = canAccessCourses;
      break;
    case 'settings':
      hasAccess = canAccessSettings && AppConfig.userSettingsEnabled;
      break;
    case 'aiRecommendation':
      hasAccess = canAccessAiRecommendation;
      break;
    case 'trainingPlanGateway':
      hasAccess = canAccessTrainingPlanGateway;
      break;
  }

  console.log(`ProtectedRoute: requiredAccess=${requiredAccess}, hasAccess=${hasAccess}`);

  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

